"use client";

import { useEffect } from "react";
import type { UseFormSetValue, UseFormTrigger } from "react-hook-form";
import { type Translationtype, STATE_MAX_LENGTH, findStateOption } from "utils";
import { useCountryStates } from "../hooks/useCountryStates";
import { FormInput } from "./form-input";
import { FormSelect } from "./form-select";

interface IFormStateFieldProps {
  register: any;
  errors: any;
  name: string;
  /** ISO 3166-1 alpha-2 code of the country the address belongs to. */
  countryCode?: string;
  /** Current form value of this field, normally `watch(name)`. */
  currentValue?: string;
  className?: string;
  disabled?: boolean;
  isRequired?: boolean;
  title: string;
  setValue?: UseFormSetValue<any>;
  trigger?: UseFormTrigger<any>;
  t: Translationtype;
}

/** Looks like "US-CA": a subdivision code that belongs to a specific country. */
const isoCodePattern = /^[A-Za-z]{2}-/;

/**
 * Renders the administrative subdivision of an address. Countries with a known ISO 3166-2 list
 * (United States, Canada, Australia, Hungary, Romania, Austria) get a dropdown, every other country
 * gets a free text input so the state can still be filled in by hand.
 *
 * The dropdown works with the full ISO code ("US-AZ"); the backend stores only the subdivision part
 * ("AZ"), which is what a saved address comes back with and is mapped onto its option here.
 */
export function FormStateField({
  register,
  errors,
  name,
  countryCode,
  currentValue,
  className,
  disabled,
  isRequired = false,
  title,
  setValue,
  trigger,
  t,
}: IFormStateFieldProps) {
  const { states, hasStates, isLoading } = useCountryStates(countryCode);

  useEffect(() => {
    // Never touch the value while the list is still on its way, a valid code would be wiped.
    if (isLoading || !setValue) return;

    const value = currentValue?.trim();
    if (!value) return;

    if (hasStates) {
      const match = findStateOption(states, countryCode, value);

      if (match) {
        // Re-apply once the options exist, otherwise the <select> has nothing to select and the
        // browser falls back to its first entry. A stored "AZ" becomes its option's "US-AZ".
        if (currentValue !== match.value) {
          setValue(name, match.value, { shouldDirty: false });
        }
        return;
      }

      // The value belongs to a country that is no longer selected.
      setValue(name, "", { shouldDirty: true, shouldValidate: false });
      return;
    }

    // No list for this country: keep whatever was typed, but drop a leftover subdivision code.
    if (isoCodePattern.test(value)) {
      setValue(name, "", { shouldDirty: true, shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode, isLoading, hasStates, states, currentValue]);

  if (hasStates) {
    return (
      <FormSelect
        t={t}
        register={register}
        errors={errors}
        setValue={setValue}
        trigger={trigger}
        name={name}
        options={states}
        title={title}
        noTranslate
        className={className}
        disabled={disabled}
        isRequired={isRequired}
      />
    );
  }

  return (
    <FormInput
      t={t}
      register={register}
      errors={errors}
      name={name}
      autoComplete="address-level1"
      title={title}
      className={className}
      disabled={disabled}
      isRequired={isRequired}
      maxLength={STATE_MAX_LENGTH}
    />
  );
}
